import { useNavigate } from "react-router-dom";
import { useState, useMemo, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

import {
  CandidateApplicationStatus,
  CandidateStatus,
  CompanyJobOpening,
} from "@/types";
import { useMyCompanyQuery } from "@/queries/company/my-company";
import { useCompanyOpportunityQuery } from "@/queries/company/opportunities";

import { AccordionItem, AccordionContent, Accordion } from "../ui/accordion";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import {
  Building,
  Calendar1,
  ChartPie,
  Clipboard,
  Clock4,
  Clock5,
  DollarSign,
  Loader2,
  MapPin,
  User,
} from "lucide-react";
import { Button } from "../ui/button";
import { AddOpportunityDialog } from "../dialogs/company/opportunity/add-opportunity";
import { ChatWindowDialogHandle } from "../forms/chat";
import PaymentButton from "../dialogs/company/opportunity/payment";

import dateStringToLocalDate from "@/utils/dateStringToLocalDate";
import formatBudgetRange from "@/utils/formatBudgetRange";

import { useConversationsQuery } from "@/queries/chat/user-conversation";
import { CompanyCandidateControls } from "./company-candidate-controls";

const STATUS_ORDER: CandidateApplicationStatus[] = [
  "Accepted by Client",
  "Contracting",
  "Making Offer",
  "Shortlisted",
  "Rejected for Role",
  "Not Interested",
] as const;

// Return the index of the status field
const getStatusOrder = (status?: CandidateApplicationStatus): number => {
  const index = status ? STATUS_ORDER.indexOf(status) : -1;
  return index === -1 ? STATUS_ORDER.length : index;
};

const orderCandidates = (a: CandidateStatus, b: CandidateStatus): number => {
  // Sorting function for two candidates, ordering by status and then alphabetically
  const rankA = getStatusOrder(a.status);
  const rankB = getStatusOrder(b.status);

  if (rankA !== rankB) {
    return rankA - rankB;
  }

  // Move candidates that had interviews requested in front of ones that haven't
  if (a.interviews.length && !b.interviews.length) {
    return -1;
  } else if (!a.interviews.length && b.interviews.length) {
    return 1;
    // Sort undefined or empty string names to the end
  } else if (!a.name && !b.name) {
    return 0;
  } else if (!a.name) {
    return 1;
  } else if (!b.name) {
    return -1;
  }

  return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
};

export const CompanyOpportunityRow = ({
  id,
  opportunity,
  highlight,
}: {
  id: number;
  opportunity: CompanyJobOpening;
  highlight?: boolean;
}) => {
  const { company, companyLoading } = useMyCompanyQuery();
  const { opportunityData, opportunityLoading } = useCompanyOpportunityQuery(
    opportunity.id,
  );
  const { chatConversations } = useConversationsQuery();

  const [addOpportunityOpen, setAddOpportunityOpen] = useState<boolean>(false);
  const [openItem, setOpenItem] = useState<string | undefined>(undefined);

  const rowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (highlight) {
      setOpenItem(id.toString());
      requestAnimationFrame(() => {
        rowRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [highlight, id]);

  const orderedCandidates = useMemo(() => {
    if (
      opportunity.status === "Role Filled (won)" ||
      opportunity.status === "Role Filled (lost)"
    ) {
      return opportunityData?.candidates
        ?.filter((candidate) => candidate.status === "Accepted by Client")
        .sort(orderCandidates);
    }
    return opportunityData?.candidates?.sort(orderCandidates);
  }, [opportunityData?.candidates, opportunity.status]);

  const hasMessageWaiting = useMemo(
    () =>
      chatConversations?.some(
        (conversation) =>
          // A candidate is a participant in a conversation
          conversation.participants.some((participant) =>
            opportunityData?.candidates?.some(
              (candidate) => candidate.userId === participant.userId,
            ),
          ) &&
          // The conversation is for this job opening
          conversation.jobOpeningId === opportunity.id &&
          // The conversation has at least one message
          conversation.lastMessage &&
          // User hasn't seen any of the messages
          (!conversation.lastSeenAt ||
            // Or they haven't seen the most recent one (at least)
            conversation.lastMessage.createdAt > conversation.lastSeenAt),
      ),
    [chatConversations, opportunityData, opportunity.id],
  );

  const activeOfferCandidate = useMemo(() => {
    const activeCandidates = orderedCandidates?.filter((candidate) => {
      if (candidate.billing) {
        return true;
      }

      const offer = candidate.offers.at(-1);
      if (offer && !["rejected", "withdrawn"].includes(offer.status)) {
        return true;
      }
    });

    if (!activeCandidates?.length) {
      return;
    } else if (activeCandidates.length === 1) {
      return activeCandidates[0];
    } else {
      // Fine if the company hired multiple candidates, forces them to view offers/payments individually
    }
  }, [orderedCandidates]);

  const offer = activeOfferCandidate?.offers.at(-1);
  const billing = activeOfferCandidate?.billing;

  const formattedBudget =
    formatBudgetRange(opportunity.budgetMinCents, opportunity.budgetMaxCents) ??
    opportunity.budgetNotes;

  let budgetString: string | undefined;
  if (!formattedBudget) {
    // No-op
  } else if (opportunity.type === "Project" && opportunity.budgetType) {
    budgetString = `${formattedBudget} / ${opportunity.budgetType === "Monthly retainer" ? "mo retainer" : "project"}`;
  } else if (opportunity.type === "Fractional") {
    budgetString = `${formattedBudget} / mo`;
  } else if (opportunity.type === "Full-time") {
    budgetString = `${formattedBudget} / yr`;
  }

  return (
    <Accordion
      collapsible
      type="single"
      className="w-full rounded-xl border"
      value={openItem}
      onValueChange={setOpenItem}
      ref={rowRef}
    >
      <AccordionItem
        value={id.toString()}
        className="w-full flex flex-col border-b-0"
      >
        <div
          className={cn(
            "p-4 w-full flex flex-row",
            openItem === id.toString() && "border-b",
          )}
        >
          <Avatar
            className={
              "relative size-16 bg-background border-2 border-background shadow-md mr-4"
            }
          >
            {companyLoading ? (
              <div className="size-full bg-muted animate-pulse" />
            ) : (
              <>
                {company?.logoLink && <AvatarImage src={company?.logoLink} />}
                <AvatarFallback>
                  <Building />
                </AvatarFallback>
              </>
            )}
          </Avatar>
          <div className="w-full flex flex-col gap-2">
            <div className="w-full flex flex-col md:flex-row gap-2 md:gap-1 justify-between md:items-center">
              <div className="flex flex-col">
                <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
                  <h4 className="text-xl font-bold text-[#0a0a0a]">
                    {opportunity.role}
                  </h4>
                  <span className="w-fit rounded-full bg-border/50 border text-xs text-muted-foreground px-2 py-1">
                    {`${orderedCandidates?.length ?? 0} Candidate${orderedCandidates?.length !== 1 ? "s" : ""}`}
                  </span>
                  {hasMessageWaiting && (
                    <span
                      className="w-fit rounded-full bg-red-600 text-xs text-white px-2 py-1 cursor-pointer"
                      onClick={() =>
                        setOpenItem((old) =>
                          old === id.toString() ? "" : id.toString(),
                        )
                      }
                    >
                      Message Waiting
                    </span>
                  )}
                </div>
                <span className="flex flex-row items-center gap-1">
                  <p className="text-sm font-semibold text-orange-600">
                    {opportunity.status?.startsWith("Role Filled")
                      ? "Closed"
                      : opportunity.status}
                  </p>
                </span>
              </div>
              <div className="flex flex-row flex-wrap gap-2 md:gap-4 items-center">
                <Button
                  variant="outline"
                  onClick={() =>
                    setOpenItem((old) =>
                      old === id.toString() ? "" : id.toString(),
                    )
                  }
                >
                  {openItem === id.toString()
                    ? "Hide Candidates"
                    : "See Candidates"}
                </Button>
                {opportunity.status !== "Role Filled (won)" &&
                opportunity.status !== "Role Filled (lost)" ? (
                  <AddOpportunityDialog
                    open={addOpportunityOpen}
                    setOpen={setAddOpportunityOpen}
                    opening={opportunityData ?? opportunity}
                    trigger={
                      <Button
                        variant={activeOfferCandidate ? "outline" : "secondary"}
                      >
                        View & Edit Job
                      </Button>
                    }
                  />
                ) : null}
                {billing ? (
                  <PaymentButton
                    opportunity={opportunity}
                    billing={billing}
                    oldButtonType
                  />
                ) : offer ? (
                  <PaymentButton
                    opportunity={opportunity}
                    offer={offer}
                    oldButtonType
                  />
                ) : null}
              </div>
            </div>
            <div className="flex flex-row flex-wrap gap-4 self-stretch items-center">
              {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
              {opportunity.scopeTime || opportunity.scopeEngagementDuration ? (
                <div className="flex flex-row gap-2 items-center">
                  <Clock4 className="stroke-muted-foreground" size={16} />
                  <span className="text-sm text-muted-foreground">
                    {[
                      opportunity.scopeTime,
                      opportunity.scopeEngagementDuration,
                    ]
                      .filter((v) => Boolean(v))
                      .join(" | ")}
                  </span>
                </div>
              ) : undefined}
              {Boolean(budgetString) && (
                <div className="col-span-2 flex flex-row gap-2 items-center">
                  <DollarSign className="stroke-muted-foreground" size={16} />
                  <span className="text-sm text-muted-foreground">
                    {budgetString}
                  </span>
                </div>
              )}
              <div className="flex flex-row gap-2 items-center">
                <Calendar1 className="stroke-muted-foreground" size={16} />
                <span className="text-sm text-muted-foreground">
                  {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
                  {opportunity.desiredStartDate
                    ? dateStringToLocalDate(
                        opportunity.desiredStartDate,
                      )?.toLocaleDateString("en-US", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })
                    : "Unknown"}
                </span>
              </div>
              <span
                className={cn(
                  "w-fit px-2 py-1",
                  "flex flex-row items-center gap-1",
                  "text-xs font-semibold",
                  opportunity.type === "Fractional"
                    ? "bg-purple-200 text-purple-600 stroke-purple-600"
                    : opportunity.type === "Project"
                      ? "bg-green-200 text-green-600 stroke-green-600"
                      : opportunity.type === "Full-time"
                        ? "bg-blue-200 text-blue-600 stroke-blue-600"
                        : undefined,
                )}
              >
                {opportunity.type === "Fractional" ? (
                  <>
                    <ChartPie size={12} />
                    Fractional
                  </>
                ) : opportunity.type === "Project" ? (
                  <>
                    <Clipboard size={12} />
                    Project
                  </>
                ) : opportunity.type === "Full-time" ? (
                  <>
                    <Clock5 size={12} />
                    Full-time
                  </>
                ) : undefined}
              </span>
            </div>
          </div>
        </div>
        <AccordionContent className="w-full pb-0">
          {opportunityLoading || !opportunityData ? (
            <Loader2 className="animate-spin mx-auto" />
          ) : orderedCandidates && orderedCandidates.length > 0 ? (
            <div className="w-full flex flex-col">
              {orderedCandidates.map((candidate) => (
                <CompanyOpportunityCandidate
                  key={candidate.id}
                  candidate={candidate}
                  opportunity={opportunityData}
                />
              ))}
            </div>
          ) : (
            <div className="w-full p-4">
              <p className="w-full text-xs text-muted-foreground text-center">
                No candidates yet- check back later!
              </p>
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

const CompanyOpportunityCandidate = ({
  candidate,
  opportunity,
}: {
  opportunity: CompanyJobOpening;
  candidate: CandidateStatus;
}) => {
  const navigate = useNavigate();

  const { chatConversations } = useConversationsQuery();
  const { company } = useMyCompanyQuery();

  const chatRef = useRef<ChatWindowDialogHandle>(null);

  const mostRecentInterview = useMemo(
    () => candidate.interviews.at(-1),
    [candidate.interviews],
  );

  const stage = useMemo<
    | "Rejected"
    | "Hired"
    | "Contracting"
    | "Offer Made"
    | `Interview${string}`
    | "Matched"
  >(() => {
    if (candidate.status === "Rejected for Role") {
      return "Rejected";
    } else if (candidate.status === "Accepted by Client") {
      return "Hired";
    } else if (candidate.status === "Contracting") {
      return "Contracting";
    } else if (candidate.status === "Making Offer") {
      return "Offer Made";
    } else if (!mostRecentInterview) {
      return "Matched";
    } else if (mostRecentInterview.isCompleted) {
      return `Interview "${mostRecentInterview.name}" Complete`;
    } else if (!mostRecentInterview.scheduled) {
      return `Interview "${mostRecentInterview.name}" Requested (Awaiting Scheduling)`;
    } else if (mostRecentInterview.startTime) {
      return `Interview "${mostRecentInterview.name}" Scheduled on ${new Date(mostRecentInterview.startTime).toLocaleString("en-US")}`;
    } else {
      return `Interview "${mostRecentInterview.name}" Scheduled`;
    }
  }, [mostRecentInterview, candidate]);

  const anonymousCandidate = useMemo(() => {
    return (
      candidate.status !== "Screening" &&
      candidate.status !== "Possible Fit" &&
      candidate.status !== "Shortlisted" &&
      candidate.status !== "Making Offer" &&
      candidate.status !== "Accepted by Client" &&
      candidate.status !== "Rejected for Role"
    );
  }, [candidate.status]);

  // See if there is an existing conversation between the user and the company
  const conversation = chatConversations?.find(
    (conversation) =>
      conversation.jobOpeningId === opportunity.id &&
      conversation.participants.length === 2 &&
      conversation.participants.every(
        (participant) =>
          participant.companyId === company?.id ||
          participant.userId === candidate.userId,
      ),
  );

  const hasMessageWaiting = useMemo(
    () =>
      // The conversation has at least one message
      conversation?.lastMessage &&
      // User hasn't seen any of the messages
      (!conversation.lastSeenAt ||
        // Or they haven't seen the most recent one (at least)
        conversation.lastMessage.createdAt > conversation.lastSeenAt),
    [conversation],
  );

  return (
    <div
      key={candidate.id}
      className="w-full flex flex-col flex-wrap gap-2 md:flex-row md:gap-x-0 justify-between md:items-center p-4 border-b last-of-type:border-b-0"
    >
      <div className="flex flex-row md:items-center gap-4">
        <Avatar
          className={
            "relative size-12 bg-background border-2 border-background shadow-md"
          }
        >
          {candidate.profilePictureLink && (
            <AvatarImage src={candidate.profilePictureLink} />
          )}
          <AvatarFallback>
            <User />
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-col">
          <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
            <h5
              className={cn(
                "text-lg font-semibold",
                !anonymousCandidate && "hover:underline cursor-pointer",
              )}
              onClick={() => {
                if (anonymousCandidate) {
                  return;
                }

                const url = new URL(window.location.href);
                url.searchParams.set("focus", opportunity.id);
                window.history.replaceState({}, "", url.toString());
                navigate(
                  `/opening/${opportunity.id}/application/${candidate.id}`,
                );
              }}
            >
              {candidate.name}
            </h5>
            {hasMessageWaiting && (
              <span
                className="w-fit rounded-full bg-red-600 text-xs text-white px-2 py-1 cursor-pointer"
                onClick={() => setTimeout(() => chatRef.current?.open(), 0)}
              >
                Message Waiting
              </span>
            )}
          </div>
          <div className="flex flex-row items-center gap-x-1 md:gap-x-0 flex-wrap md:flex-nowrap">
            <span className="flex flex-row items-center gap-1 md:pr-2">
              <p
                className={`text-sm font-semibold ${stage === "Matched" ? "text-purple-600" : stage === "Hired" ? "text-green-600" : stage === "Rejected" ? "text-red-600" : "text-orange-600"}`}
              >
                {stage}
              </p>
            </span>
            {Boolean(candidate.headline) && (
              <div className="flex flex-row items-center gap-2 md:border-l md:px-2">
                <User className="size-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {candidate.headline}
                </p>
              </div>
            )}
            {Boolean(candidate.location) && (
              <div className="col-span-2 flex flex-row items-center gap-2 md:border-l md:pl-2">
                <MapPin className="size-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {candidate.location}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <CompanyCandidateControls
        candidate={candidate}
        opportunity={opportunity}
        chatRef={chatRef}
        enableProfileLink
      />
    </div>
  );
};
